import React from "react";
import { useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useApiMutation, useApiQuery } from "../../hooks/useApi";
import { useReactToPrint } from "react-to-print";

const mockBanks = [
  "Union Bank Fiscale (Advisor)",
  "Union Bank Fiscale (Solutions)",
  "Canara Fiscale (Advisor II)",
  "Other"
];

const mockCash = [
  "Cash (Advisor)",
  "Cash (Solutions)",
  "Cash (Advisor II)",
];

function AddFeeReceiptsPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [selectedClient, setSelectedClient] = useState(null);
  const [savedReceipt, setSavedReceipt] = useState(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      paymentMethod: "cash",
      bank: "",
    }
  });

  const createReceiptMutation = useApiMutation("/receipts", "POST", {
    onSuccess: (data) => {
      setLoading(false);
      setMessage({ type: "success", text: "Receipt added successfully!" });

      // VERY IMPORTANT – store the saved receipt
      setSavedReceipt(data.receipt || data);

      reset({
        amount: "",
        paymentMethod: "cash",
        bank: "",
        transactionReference: "",
        notes: "",
      });
    },
    onError: () => {
      setLoading(false);
      setMessage({ type: "error", text: "Failed to add receipt. Please try again." });
    }
  });

  const { data: clientsData, isLoading: clientsLoading } = useApiQuery(["clients"], "/clients/");
  const clients = clientsData?.clients || [];

  const onSubmit = (data) => {
    if (!selectedClient) {
      setMessage({ type: "error", text: "Please select a client first" });
      return;
    }

    // Validate bank field for payment methods that require it
    const requiresBankField = ["bank_transfer", "cheque", "upi", "card", "cash"];
    if (requiresBankField.includes(data.paymentMethod) && !data.bank) {
      setMessage({
        type: "error",
        text: `Please select a ${data.paymentMethod === "cash" ? "cash method" : "bank"} for ${data.paymentMethod.replace("_", " ")}`
      });
      return;
    }

    const receiptData = {
      clientId: selectedClient._id,
      amount: parseFloat(data.amount),
      paymentMethod: data.paymentMethod,
      bank: data.bank || undefined,
      transactionReference: data.transactionReference || undefined,
      notes: data.notes || undefined,
    };

    setLoading(true);
    createReceiptMutation.mutate(receiptData);
  };

  const handleClientSelect = (event, value) => {
    setSelectedClient(value);
    setSavedReceipt(null); // reset receipt preview
  };

  const getOptionLabel = (option) =>
    option ? `${option.name} - ${option.phoneNumber}` : "";

  const filterOptions = (options, { inputValue }) => {
    const s = inputValue.toLowerCase();
    return options.filter((o) =>
      o.name?.toLowerCase().includes(s) ||
      o.phoneNumber?.toLowerCase().includes(s) ||
      o.email?.toLowerCase().includes(s)
    );
  };

  const watchedPaymentMethod = watch("paymentMethod");

  // Determine which bank options to show based on payment method
  const getBankOptions = () => {
    if (watchedPaymentMethod === "cash") {
      return mockCash;
    }
    return mockBanks;
  };

  // Determine if bank field should be shown
  const shouldShowBankField = () => {
    return ["cash", "bank_transfer", "cheque", "upi", "card"].includes(watchedPaymentMethod);
  };

  // Get label for bank field
  const getBankFieldLabel = () => {
    if (watchedPaymentMethod === "cash") {
      return "Cash Method";
    }
    return "Bank";
  };

  /** PRINT HANDLER **/
  const receiptRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: `Receipt_${savedReceipt?.receiptNumber || "Unknown"}`,
  });

  const PrintableReceipt = React.forwardRef(({ receipt, client }, ref) => (
    <div
      ref={ref}
      style={{
        padding: "40px",
        width: "420px",
        margin: "0 auto",
        fontFamily: "monospace",
      }}
    >
      <style>{`
        @media print {
          @page {
            size: A5;
            margin: 10mm;
          }
        }
      `}</style>

      <h2 style={{ textAlign: "center" }}>Fiscale</h2>
      <p style={{ fontSize: "12px", textAlign: "center", marginBottom: "20px" }}>Koach Gaon, Biswanath Chariali, Assam, 784176, +91 6901945275</p>
      <p><strong>Receipt No:</strong> {receipt?.receiptNumber}</p>
      <p><strong>Date:</strong> {new Date(receipt?.collectedDate || Date.now()).toLocaleDateString()}</p>
      <p><strong>Amount:</strong> ₹{receipt?.amount}</p>
      <p><strong>Client Name:</strong> {client?.name}</p>
      <p><strong>Phone Number:</strong> {client?.phoneNumber}</p>
      {/* <p><strong>Mode of Payment:</strong> {receipt?.paymentMethod}</p> */}
      {receipt?.bank && (
        <p><strong>{receipt?.paymentMethod === "cash" ? "Cash Method:" : "Bank:"}</strong> {receipt.bank}</p>
      )}
      <p><strong>Collected By:</strong> {receipt?.collectedBy?.name || "—"}</p>
      <p style={{ fontSize: "10px" }}>Received with thanks an amount of Rs. {receipt?.amount}/- (One Thousand Five Hundred only) from {client?.name}. Cheques are subject to realisation.</p>
      <hr style={{ margin: "20px 0" }} />

      <p style={{ textAlign: "center", opacity: 0.7 }}>Thank you for choosing Fiscale</p>
    </div>
  ));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Add Fee Receipt
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* CLIENT SELECTION */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Select Client
            </Typography>

            <Autocomplete
              options={clients}
              value={selectedClient}
              onChange={handleClientSelect}
              getOptionLabel={getOptionLabel}
              filterOptions={filterOptions}
              loading={clientsLoading}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Client"
                  placeholder="Name, phone, email..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {selectedClient && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Client Details
                  </Typography>
                  <Typography><strong>Name:</strong> {selectedClient.name}</Typography>
                  <Typography><strong>Phone:</strong> {selectedClient.phoneNumber}</Typography>
                  {selectedClient.panNumber && (
                    <Typography><strong>PAN:</strong> {selectedClient.panNumber}</Typography>
                  )}
                </CardContent>
              </Card>
            )}
          </Paper>
        </Grid>

        {/* RECEIPT FORM */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Receipt Details
            </Typography>

            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    {...register("amount", {
                      required: "Amount is required",
                      min: { value: 0, message: "Amount must be greater than or equal to 0" }
                    })}
                    disabled={!selectedClient}
                    error={!!errors.amount}
                    helperText={errors.amount?.message}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="paymentMethod"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth disabled={!selectedClient}>
                        <InputLabel>Payment Method</InputLabel>
                        <Select {...field}>
                          <MenuItem value="cash">Cash</MenuItem>
                          <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                          <MenuItem value="cheque">Cheque</MenuItem>
                          <MenuItem value="upi">UPI</MenuItem>
                          <MenuItem value="card">Card</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                {shouldShowBankField() && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>{getBankFieldLabel()}</InputLabel>
                      <Select {...register("bank")} disabled={!selectedClient}>
                        {getBankOptions().map((option) => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Transaction Reference"
                    {...register("transactionReference")}
                    disabled={!selectedClient}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    {...register("notes")}
                    disabled={!selectedClient}
                  />
                </Grid>
              </Grid>

              {/* ACTION BUTTONS */}
              <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={!selectedClient || loading}
                >
                  {loading ? "Saving..." : "Add Receipt"}
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate("/dashboard")}
                >
                  Cancel
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  disabled={!savedReceipt}
                  onClick={handlePrint}
                >
                  Print Receipt
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* RECEIPT PREVIEW */}
        {savedReceipt && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, mt: 2 }}>
              <PrintableReceipt
                ref={receiptRef}
                receipt={savedReceipt}
                client={selectedClient}
              />
            </Paper>
          </Grid>
        )}

      </Grid>
    </Box>
  );
}

export default AddFeeReceiptsPage;